import { GetStaticPropsContext } from 'next/types';

import { Box } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { PageCategory } from '~/features/analytics/constants';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { LegalPageBanner } from '~/features/legal/components/legal-page-banner';
import { LegalSection } from '~/features/legal/components/legal-section';
import { getLegalDocuments } from '~/features/legal/controllers/get-legal-documents';
import { LegalDocumentMetadata } from '~/features/legal/models/legal-documents-metadata';
import { getMetadataBySection } from '~/features/legal/utils/get-metadata-by-section';
import { logger } from '~/features/logging/logger';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';

export default function LegalPage({ page, legalDocuments }: { page: Page; legalDocuments: LegalDocumentMetadata[] }) {
  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.Legal} />
      <PageLayout withoutVerticalPadding>
        <Box bgColor='grey.50'>
          <LegalPageBanner />

          <ContentContainer
            paddingBottom='120px'
            paddingTop='80px'
            sx={{ '> *:not(:last-child)': { mb: '80px', scrollMarginTop: '64px' } }}
          >
            <LegalSection
              glyphName='clipboard-two'
              legalDocumentsMetadata={getMetadataBySection(legalDocuments, 'termsAndPrivacy')}
              section='termsAndPrivacy'
              title='Terms & Privacy'
            />

            <LegalSection
              glyphName='book-one'
              legalDocumentsMetadata={getMetadataBySection(legalDocuments, 'communityCodesOfConduct')}
              section='communityCodesOfConduct'
              title='Community Codes of Conduct'
            />

            <LegalSection
              glyphName='trophy-three'
              legalDocumentsMetadata={getMetadataBySection(legalDocuments, 'contestsAndSweepStakes')}
              section='contestsAndSweepStakes'
              title='Contests & Sweepstakes'
            />

            <LegalSection
              glyphName='hand-plant-one'
              legalDocumentsMetadata={getMetadataBySection(legalDocuments, 'scholarships')}
              section='scholarships'
              title='Scholarships'
            />

            <LegalSection
              glyphName='suitcase-three'
              legalDocumentsMetadata={getMetadataBySection(legalDocuments, 'services')}
              section='services'
              title='Services'
            />

            <LegalSection
              glyphName='e-commerce-storefront-one'
              legalDocumentsMetadata={getMetadataBySection(legalDocuments, 'vendorsAndSuppliers')}
              section='vendorsAndSuppliers'
              title='Vendors & Suppliers'
            />

            <LegalSection
              glyphName='lightbulb-head-three'
              legalDocumentsMetadata={getMetadataBySection(legalDocuments, 'intellectualProperty')}
              section='intellectualProperty'
              title='Intellectual Property'
            />
          </ContentContainer>
        </Box>
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  if (!locale) {
    logger.fatal('Legal locale not found', {
      file: 'src/pages/legal/[slug].tsx',
      function: 'getStaticProps',
      locale,
    });

    throw new Error('Locale not found');
  }

  const [page, legalDocuments] = await Promise.all([getPage('/legal'), getLegalDocuments(locale)]);

  return {
    props: {
      page,
      legalDocuments,
    },
  };
}
